import * as React from 'react';

import { NoteColor, NoteType } from '../constants';

import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

interface Props {
    readonly x: number,
    readonly y: number,
    readonly text: string,
    readonly color: NoteColor,
    readonly type: NoteType,
    readonly dragging: boolean,
    readonly onMouseDown: (e: React.MouseEvent<SVGElement>) => void,
    readonly onDoubleClicked: () => void,
    readonly onDeleteButtonClicked: () => void,
}

const styles = (theme: Theme) => createStyles({
    animated: {
        transition: 'transform 100ms'
    },
    deleteButton: {
        fill: theme.palette.grey[600],
        fontFamily: 'FontAwesome',
        fontSize: 24,
        textAnchor: 'start',
        visibility: 'hidden',
        cursor: 'pointer',
        '&:hover': {
            fill: theme.palette.grey[700]
        },
    },
    draggedContent: {
        transform: 'scale(1.03, 1.03)'
    },
    draggedShadow: {
        transform: 'scale(1.07, 1.07) translate(3px, 10px)'
    },
    root: {
        '&:hover $deleteButton': {
            visibility: 'visible'
        },
    },
    rootNoDrag: {
        cursor: 'grab',
    },
    rootDrag: {
        cursor: 'grabbing',
    },
    textBox: {
        fill: theme.palette.common.black,
        fontFamily: '"Gloria Hallelujah", cursive',
        fontSize: '20pt',
        fontWeight: 'bold',
        textAnchor: 'start',
        width: 210,
        height: 250,
        overflow: 'hidden',
        userSelect: 'none',
        '&:selection': {
            background: 'none'
        },
        whiteSpace: 'pre-wrap',
    },
});

type Styles = 'root' | 'rootNoDrag' | 'rootDrag' | 'animated' | 'draggedShadow' | 'draggedContent' | 'textBox' | 'deleteButton';


function stopPropagation(event: React.MouseEvent<SVGElement>) {
    event.stopPropagation();
    event.preventDefault();
}

const NoteView = ({ text, x, y, type, color, dragging, classes, onMouseDown, onDoubleClicked, onDeleteButtonClicked }: Props & WithStyles<Styles>) => (
    <g
        className={`${classes.root} ${dragging? classes.rootDrag : classes.rootNoDrag }`}
        transform={`translate(${x} ${y})`}
        onMouseDown={onMouseDown}
        onDoubleClick={e => {
            stopPropagation(e);
            onDoubleClicked();
        }}
    >
        {(() => {
            switch (type) {
                default:
                    return (
                        <g>
                            <image
                                className={`${classes.animated} ${dragging ? classes.draggedShadow : ''}`}
                                xlinkHref="images/shadow_note_0.png"
                                width={259}
                                height={254}
                                x={-6}
                                y={-1}
                                preserveAspectRatio="none"
                            />
                            <g className={`${classes.animated} ${dragging ? classes.draggedContent : ''}`}>
                                <use xlinkHref={`#Template_${color}_${type}`}/>
                                <foreignObject x={20} y={-15} width={220} height={250}>
                                    <p className={classes.textBox}>{text}</p>
                                </foreignObject>
                                <g
                                    className={classes.deleteButton}
                                    transform="translate(210 13)"
                                    onClick={onDeleteButtonClicked}
                                    onMouseDown={stopPropagation}
                                    onDoubleClick={stopPropagation}
                                >
                                    <rect fill="transparent" x={-3} y={-3} width={30} height={30}/>
                                    <path
                                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                                    />
                                    <path d="M0 0h24v24H0z" fill="none"/>
                                </g>
                            </g>
                        </g>
                    );
                case NoteType.T1:
                    return (
                        <g>
                            <image
                                className={`${classes.animated} ${dragging ? classes.draggedShadow : ''}`}
                                xlinkHref="images/shadow_note_1.png"
                                width={278}
                                height={280}
                                x={-2}
                                y={-2}
                                preserveAspectRatio="none"
                            />
                            <g className={`${classes.animated} ${dragging ? classes.draggedContent : ''}`}>
                                <use xlinkHref={`#Template_${color}_${type}`}/>
                                <foreignObject x={10} y={10} transform="rotate(-7 0 0)" width={200} height={200}>
                                    <p className={classes.textBox}>{text}</p>
                                </foreignObject>
                                <g
                                    className={classes.deleteButton}
                                    transform="translate(210 16) rotate(-7 0 0)"
                                    onClick={onDeleteButtonClicked}
                                    onMouseDown={stopPropagation}
                                    onDoubleClick={stopPropagation}
                                >
                                    <rect fill="transparent" x={-3} y={-3} width={30} height={30}/>
                                    <path
                                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                                    />
                                    <path d="M0 0h24v24H0z" fill="none"/>
                                </g>
                            </g>
                        </g>
                    );
                case NoteType.T2:
                    return (
                        <g>
                            <image
                                className={`${classes.animated} ${dragging ? classes.draggedShadow : ''}`}
                                xlinkHref="images/shadow_note_2.png"
                                width={278}
                                height={282}
                                x={-7}
                                y={-4}
                                preserveAspectRatio="none"
                            />
                            <g className={`${classes.animated} ${dragging ? classes.draggedContent : ''}`}>
                                <use xlinkHref={`#Template_${color}_${type}`}/>
                                <foreignObject x={40} y={-20} transform="rotate(4 0 0)" width={200} height={200}>
                                    <pre className={classes.textBox}>{text}</pre>
                                </foreignObject>
                                <g
                                    className={classes.deleteButton}
                                    transform="translate(230 25) rotate(4 0 0)"
                                    onClick={onDeleteButtonClicked}
                                    onMouseDown={stopPropagation}
                                    onDoubleClick={stopPropagation}
                                >
                                    <rect fill="transparent" x={-3} y={-3} width={30} height={30}/>
                                    <path
                                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                                    />
                                    <path d="M0 0h24v24H0z" fill="none"/>
                                </g>
                            </g>
                        </g>
                    );
                case NoteType.T3:
                    return (
                        <g>
                            <image
                                className={`${classes.animated} ${dragging ? classes.draggedShadow : ''}`}
                                xlinkHref="images/shadow_note_3.png"
                                width={262}
                                height={267}
                                x={-10}
                                y={-5}
                                preserveAspectRatio="none"
                            />
                            <g className={`${classes.animated} ${dragging ? classes.draggedContent : ''}`}>
                                <use xlinkHref={`#Template_${color}_${type}`}/>
                                <foreignObject x={25} y={-20} transform="rotate(3 0 0)" width={200} height={200}>
                                    <p className={classes.textBox}>{text}</p>
                                </foreignObject>
                                <g
                                    className={classes.deleteButton}
                                    transform="translate(215 25) rotate(3 0 0)"
                                    onClick={onDeleteButtonClicked}
                                    onMouseDown={stopPropagation}
                                    onDoubleClick={stopPropagation}
                                >
                                    <rect fill="transparent" x={-3} y={-3} width={30} height={30}/>
                                    <path
                                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                                    />
                                    <path d="M0 0h24v24H0z" fill="none"/>
                                </g>
                            </g>
                        </g>
                    );
            }
        })()}
    </g>
);

export default withStyles(styles)(NoteView);



// WEBPACK FOOTER //
// ./src/components/NoteView.tsx